/**
 * useRegisterOverlay
 * ------------------
 * Tells the global UI-overlay registry that a particular right-drawer /
 * popover is currently on screen. The Noki Copilot launcher subscribes to
 * the registry and collapses its panel while any overlay is open — without
 * tearing down the WebRTC session.
 *
 * Usage:
 *   useRegisterOverlay("start_encounter_drawer", open);
 *   useRegisterOverlay("consent_drawer", consentPopup);
 *
 * Lifecycle:
 *   • While `isOpen === true`, the overlay is registered. The hook also
 *     auto-unregisters on component unmount even if the parent forgot to
 *     flip `isOpen` first — so a route change mid-overlay still leaves a
 *     clean slate.
 *   • While `isOpen === false`, the overlay is unregistered. Re-flipping
 *     `isOpen` re-registers. Both transitions are idempotent in the slice,
 *     so the hook is safe under React StrictMode double-effects.
 *
 * Contract: `id` must be a stable, unique string per overlay instance.
 * Don't generate one per render. If two simultaneous overlays use the
 * same id, the registry treats them as one (registerOverlay is idempotent
 * on the id), which would cause the launcher to reopen too early when one
 * of them closes. Use distinct ids per overlay class.
 */

import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  registerOverlay,
  unregisterOverlay,
} from "redux/reducers/UiOverlaysSlice";

export function useRegisterOverlay(id, isOpen) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!id || typeof id !== "string") return undefined;
    if (!isOpen) return undefined;

    dispatch(registerOverlay(id));
    return () => {
      dispatch(unregisterOverlay(id));
    };
  }, [dispatch, id, isOpen]);
}

export default useRegisterOverlay;
